// attendanceSeed.js
// Stateful LocalStorage seed data and utility engine for the Egurukul ERP Attendance Module.

// Default classes, sections, and subjects if backend returns empty
export const DEFAULT_CLASSES = [
  { classId: "c1", className: "Grade 10", sectionName: "A", name: "Grade 10-A" },
  { classId: "c2", className: "Grade 10", sectionName: "B", name: "Grade 10-B" },
  { classId: "c3", className: "Grade 9", sectionName: "A", name: "Grade 9-A" },
  { classId: "c4", className: "Grade 11", sectionName: "A", name: "Grade 11-A" }
];

export const DEFAULT_SUBJECTS = [
  { courseCode: "MATH101", courseName: "Mathematics", period: "1st Period (08:30 AM)" },
  { courseCode: "SCI102", courseName: "Science", period: "2nd Period (09:30 AM)" },
  { courseCode: "ENG103", courseName: "English", period: "3rd Period (10:45 AM)" },
  { courseCode: "HIST104", courseName: "History", period: "5th Period (01:00 PM)" }
];

// Student database covering different classes
export const DEFAULT_STUDENTS = {};

// Configurable Rules Settings
export const DEFAULT_SETTINGS = {
  approvalRequired: true, // Requires Admin approval for correction requests
  defaulterThreshold: 75, // Defaulter percentage limit (< 75%)
  allowFutureDates: false, // Prevent marking future dates
  notifyAbsence: true, // Notify parents on absence (placeholder hooks)
  halfDayLateThreshold: 2 // Late count to count as one half-day absent (for percentage calculations)
};

// Generates 30 days of past attendance history
export const generateDefaultAttendanceHistory = () => {
  return {};
};

// Pre-seeded Correction Requests
export const DEFAULT_CORRECTIONS = [];

// Pre-seeded Audit Logs
export const DEFAULT_AUDIT_LOGS = [];

// ==========================================
// LOCAL STORAGE GETTERS AND SETTERS
// ==========================================

export const getStoredAttendance = () => {
  try {
    const data = localStorage.getItem("egurukul:attendance");
    if (!data) {
      const seeded = generateDefaultAttendanceHistory();
      try {
        localStorage.setItem("egurukul:attendance", JSON.stringify(seeded));
      } catch (innerErr) {
        console.warn("Failed to write initial seeded attendance to localStorage:", innerErr);
      }
      return seeded;
    }
    return JSON.parse(data);
  } catch (err) {
    console.error("Failed to parse/read attendance from localStorage:", err);
    return {};
  }
};

export const saveStoredAttendance = (attendance) => {
  try {
    localStorage.setItem("egurukul:attendance", JSON.stringify(attendance));
  } catch (err) {
    console.error("Failed to save attendance to localStorage:", err);
  }
};

export const getStoredCorrections = () => {
  try {
    const data = localStorage.getItem("egurukul:attendance_corrections");
    if (!data) {
      try {
        localStorage.setItem("egurukul:attendance_corrections", JSON.stringify(DEFAULT_CORRECTIONS));
      } catch (innerErr) {
        console.warn("Failed to write initial corrections to localStorage:", innerErr);
      }
      return DEFAULT_CORRECTIONS;
    }
    return JSON.parse(data);
  } catch (err) {
    console.error("Failed to parse/read corrections from localStorage:", err);
    return DEFAULT_CORRECTIONS;
  }
};

export const saveStoredCorrections = (corrections) => {
  try {
    localStorage.setItem("egurukul:attendance_corrections", JSON.stringify(corrections));
  } catch (err) {
    console.error("Failed to save corrections to localStorage:", err);
  }
};

export const getStoredAuditLog = () => {
  try {
    const data = localStorage.getItem("egurukul:attendance_audit");
    if (!data) {
      try {
        localStorage.setItem("egurukul:attendance_audit", JSON.stringify(DEFAULT_AUDIT_LOGS));
      } catch (innerErr) {
        console.warn("Failed to write initial audit log to localStorage:", innerErr);
      }
      return DEFAULT_AUDIT_LOGS;
    }
    return JSON.parse(data);
  } catch (err) {
    console.error("Failed to parse/read audit log from localStorage:", err);
    return DEFAULT_AUDIT_LOGS;
  }
};

export const saveStoredAuditLog = (logs) => {
  try {
    localStorage.setItem("egurukul:attendance_audit", JSON.stringify(logs));
  } catch (err) {
    console.error("Failed to save audit log to localStorage:", err);
  }
};

export const getStoredSettings = () => {
  try {
    const data = localStorage.getItem("egurukul:attendance_settings");
    if (!data) {
      try {
        localStorage.setItem("egurukul:attendance_settings", JSON.stringify(DEFAULT_SETTINGS));
      } catch (innerErr) {
        console.warn("Failed to write initial settings to localStorage:", innerErr);
      }
      return DEFAULT_SETTINGS;
    }
    return JSON.parse(data);
  } catch (err) {
    console.error("Failed to parse/read settings from localStorage:", err);
    return DEFAULT_SETTINGS;
  }
};

export const saveStoredSettings = (settings) => {
  try {
    localStorage.setItem("egurukul:attendance_settings", JSON.stringify(settings));
  } catch (err) {
    console.error("Failed to save settings to localStorage:", err);
  }
};

// helper to calculate attendance statistics
export const calculateStudentAttendanceStats = (studentId, classId, startDate = null, endDate = null) => {
  const history = getStoredAttendance();
  const settings = getStoredSettings();
  
  let totalClasses = 0;
  let presentCount = 0;
  let absentCount = 0;
  let lateCount = 0;
  let leaveCount = 0;
  
  const records = [];
  
  Object.values(history).forEach(record => {
    if (record.classId !== classId) return;
    
    // date filter
    if (startDate && record.date < startDate) return;
    if (endDate && record.date > endDate) return;
    
    const studentRecord = record.students.find(s => s.studentId === studentId);
    if (!studentRecord) return;
    
    totalClasses++;
    
    records.push({
      date: record.date,
      subjectName: record.subjectName,
      period: record.period,
      status: studentRecord.status,
      remark: studentRecord.remark
    });
    
    switch (studentRecord.status) {
      case "Present":
        presentCount++;
        break;
      case "Absent":
        absentCount++;
        break;
      case "Late":
        lateCount++;
        break;
      case "Leave":
        leaveCount++;
        break;
      default:
        break;
    }
  });
  
  // Calculate aggregate percentage
  // Late rule: e.g. every X lates count as one absence or similar
  const lateAbsencePenalty = settings.halfDayLateThreshold > 0 
    ? Math.floor(lateCount / settings.halfDayLateThreshold) * 0.5
    : 0;
    
  // Present value including late attendance (each late is fully present unless adjusted, or counted as partial)
  // Let's count Late as present but deduct any penalties if configured
  const effectivePresent = presentCount + lateCount - lateAbsencePenalty;
  
  // Leave is typically excluded from total working days, or treated as present. Let's exclude it from denominator if needed, or count as excused.
  // In typical Indian ERP systems, (Present + Excused Leave / Total Days) * 100 or Leave is just not counted as absent.
  // Let's count it as excused (does not penalize). 
  // Percentage = (effectivePresent + leaveCount) / totalClasses
  const percentage = totalClasses > 0 
    ? Math.round(((effectivePresent + leaveCount) / totalClasses) * 100)
    : 100;
    
  return {
    totalClasses,
    presentCount,
    absentCount,
    lateCount,
    leaveCount,
    percentage,
    records: records.sort((a, b) => b.date.localeCompare(a.date))
  };
};

export const seedLiveRosterHistory = (classId, subjectCode, subjectName, activeStudents) => {
  const history = getStoredAttendance();
  const today = new Date();
  
  let hasEntries = false;
  // Check if we already have entries for this class/subject in the last 30 days
  for (let i = 0; i <= 30; i++) {
    const currentDate = new Date();
    currentDate.setDate(today.getDate() - i);
    const dateStr = currentDate.toISOString().split('T')[0];
    const key = `${classId}_${dateStr}_${subjectCode}`;
    if (history[key]) {
      hasEntries = true;
      break;
    }
  }

  if (hasEntries) return; // Skip seeding if entries already exist

  console.log(`[Demo Seed] Seeding 30 days of past attendance history for class ${classId} and subject ${subjectCode}`);

  // Generate 30 days of past records
  for (let i = 30; i >= 0; i--) {
    const currentDate = new Date();
    currentDate.setDate(today.getDate() - i);
    
    // Skip Sundays
    if (currentDate.getDay() === 0) continue;
    
    const dateStr = currentDate.toISOString().split('T')[0];
    const finalKey = `${classId}_${dateStr}_${subjectCode}`;

    const studentRecords = activeStudents.map((student, idx) => {
      let status = "Present";
      let remark = "";
      
      const rand = Math.random();
      
      // Let's make some students have lower attendance for demo purposes!
      // Index 1 is a defaulter (frequent absents)
      // Index 3 has a medical leave streak in the past week
      if (idx === 1) { // Defaulter
        if (rand < 0.50) {
          status = "Present";
        } else if (rand < 0.75) {
          status = "Absent";
          remark = "Uninformed Absence";
        } else if (rand < 0.90) {
          status = "Late";
          remark = "Late by 15 mins";
        } else {
          status = "Leave";
          remark = "Family event";
        }
      } else if (idx === 3 && i >= 5 && i <= 9) { // Sick streak
        status = "Leave";
        remark = "Medical Leave";
      } else { // Normal students
        if (rand < 0.88) {
          status = "Present";
        } else if (rand < 0.93) {
          status = "Late";
          remark = "Missed school bus";
        } else if (rand < 0.97) {
          status = "Absent";
          remark = "Fever";
        } else {
          status = "Leave";
          remark = "Prior approval";
        }
      }
      
      return {
        studentId: student.id,
        studentName: student.name,
        admissionNumber: student.admissionNumber,
        status,
        remark
      };
    });

    history[finalKey] = {
      classId: classId,
      date: dateStr,
      subjectCode: subjectCode,
      subjectName: subjectName || 'General Subject',
      period: '1st Period (08:30 AM)',
      status: "final",
      markedBy: "Teacher Vinay",
      markedAt: new Date(currentDate.setHours(16, 0, 0, 0)).toISOString(),
      students: studentRecords
    };
  }

  saveStoredAttendance(history);
};
